// Shared by the browser, native URL listener and release checks. This file is
// also read by Node when preparing the static Safari Smart App Banner.
const config = require('./config.json');
const { languages } = require('../utils/config');

function routePath(pathname) {
  if (typeof pathname !== 'string' || !pathname.startsWith('/') || pathname.startsWith('//')) return null;
  let parts;
  try { parts = pathname.split('/').map(part => decodeURIComponent(part)); } catch { return null; }
  // Usernames can contain Unicode. Decode individual segments for validation,
  // but never let an encoded delimiter turn one username into another route.
  // Control characters are explicitly rejected in externally supplied URLs.
  // eslint-disable-next-line no-control-regex
  if (parts.some(part => /[/\\?#\u0000-\u001f\u007f]/.test(part))) return null;
  if (languages.includes(parts[1]) && parts[1]) parts.splice(1, 1);
  return parts.join('/') || '/';
}

function isContentPath(pathname) {
  const path = routePath(pathname);
  if (!path) return false;
  if (path === '/' || /^\/unsubscribe\/?$/.test(path)) return true;
  if (/^\/opinion\/[1-9]\d*\/?$/.test(path)) return true;
  if (/^\/news\/[1-9]\d*\/(statistic|reactions)\/?$/.test(path)) return true;
  if (/^\/user\/(settings|control)(\/|$)/i.test(path)) return false;
  return /^\/user\/[^/]+(?:\/opinion\/[1-9]\d*)?\/?$/.test(path);
}

function contentUrl(pathname, search = '') {
  if (!isContentPath(pathname)) return null;
  const url = new URL(pathname, config.origin);
  if (url.origin !== config.origin) return null;
  // Auth fragments, session tokens and native-container hints must never be
  // forwarded to a store or included in the public opening button.
  const area = new URLSearchParams(search).get('area');
  if (routePath(pathname)?.replace(/\/$/, '') !== '/unsubscribe' && ['all', 'country', 'friends'].includes(area)) url.searchParams.set('area', area);
  return url.href;
}

// Inline links may open public WIW content directly in the current router.
// Unlike nativeLinkTarget, this must never accept OAuth callbacks or a custom
// protocol from user-authored content. Destination queries are allowlisted by
// contentUrl rather than forwarded into login, settings or an editor.
function internalContentTarget(value) {
  if (typeof value !== 'string') return null;
  try {
    const url = new URL(value);
    if (url.origin !== config.origin || url.username || url.password || url.port) return null;
    const target = contentUrl(url.pathname, url.search);
    if (!target) return null;
    const parsed = new URL(target);
    return parsed.pathname + parsed.search;
  } catch { return null; }
}

function nativeLinkTarget(value) {
  if (typeof value !== 'string') return null;
  try {
    const url = new URL(value);
    const https = url.origin === config.origin;
    const custom = url.protocol === `${config.iosScheme}:` && url.hostname === new URL(config.origin).hostname;
    if ((!https && !custom) || url.username || url.password || url.port) return null;
    // Existing OAuth callbacks keep their complete query and fragment, but
    // only on our verified HTTPS origin, never through the custom scheme.
    if (https && routePath(url.pathname) === '/login') return url.pathname + url.search + url.hash;
    const target = contentUrl(url.pathname, url.search);
    if (!target) return null;
    const parsed = new URL(target);
    return parsed.pathname + parsed.search;
  } catch { return null; }
}

function installUrl(platform, options = config) {
  if (!['ios', 'android'].includes(platform)) return null;
  const value = platform === 'ios' ? options.iosInstallUrl : options.androidInstallUrl;
  if (platform === 'ios' && !value && options.iosStorePublished && /^\d+$/.test(options.iosAppId || '')) return `https://apps.apple.com/app/id${options.iosAppId}`;
  try {
    const url = new URL(value);
    const hosts = platform === 'ios' ? ['apps.apple.com', 'testflight.apple.com'] : ['play.google.com'];
    return url.protocol === 'https:' && hosts.includes(url.hostname) && !url.username && !url.password && !url.port ? url.href : null;
  } catch { return null; }
}

function openAppUrl(platform, pathname, search = '', options = config) {
  const target = contentUrl(pathname, search);
  if (!target) return null;
  if (platform === 'ios') return target.replace('https:', `${config.iosScheme}:`);
  if (platform !== 'android') return null;
  // Chrome resolves this explicit user tap against the installed TWA. No
  // timer guesses installation, and a missing app falls back to web content.
  return `intent://${target.slice('https://'.length)}#Intent;scheme=https;package=${config.androidPackage};S.browser_fallback_url=${encodeURIComponent(installUrl(platform, options) || target)};end`;
}

function smartBannerContent(pathname, search = '', options = config) {
  const target = contentUrl(pathname, search);
  return options.enabled && options.iosStorePublished && /^\d+$/.test(options.iosAppId || '') && target
    ? `app-id=${options.iosAppId}, app-argument=${target}` : null;
}

module.exports = { routePath, isContentPath, contentUrl, internalContentTarget, nativeLinkTarget, installUrl, openAppUrl, smartBannerContent };
